import React, { FC, useEffect, useState } from "react"
import { DEF_PG_SIZE_SUB } from "../../constants/maximum-page-size"
import { PRESET_SYSNAME_TYPE } from "../../constants/preset-constants"
import { TranslationKeys } from "../../contexts/TranslationKeys"
import { useLocale } from "../../contexts/localeContext"
import { TableContextProvider } from "../../contexts/table/TableContextProvider"
import presetInputColumnDescriptor from "../../models/ColumnDefinitions/PresetInputColumns"
import { CustomRowModifierWithRange } from "../../models/CustomRowAction"
import { ListData } from "../../models/ListData"
import { ListQuery } from "../../models/ListQuery"
import { Lookup } from "../../models/Lookup"
import LookupTypes from "../../models/LookupTypes"
import { getDeviceSettingsTableLegendItems } from "../../models/TableLegend"
import presetInputSearchDescriptor from "../../models/search/PresetInputSearch"
import SiteSearch from "../../models/search/SiteSearch"
import { getPresetInputsByProfileId } from "../../services/presetInputService"
import { savePreset } from "../../services/presetService"
import { getSiteNamesWithPagination } from "../../services/siteService"
import { getErrorMessage } from "../../services/utilities/ServiceError"
import { toast } from "react-toastify";
import { useDialog } from "../dialog/DialogContext"
import { SelectType } from "../settingFields/SettingSelectField"
import TableTab from "../tabs/table-tab"
import PresetSettingModal from "./presetSettingWizard/presetSettingModal"
import { SystemProfileTypeIds } from "./presetSettingsWizard"

interface Props {
    itemId: string;
    onDelete: any,
    onSave: (createNew) => void;
    profileId?: any;
    currentItemDetails?: any;
}

const PresetInputs: FC<Props> = ({ itemId, onDelete, onSave, profileId = SystemProfileTypeIds.Reader, children, currentItemDetails}) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.assignedInputs);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [presetInputId, setPresetInputId] = useState('');
    const dialog = useDialog();

    useEffect(() => {
        (async () => {
            setLookup([{
                name: LookupTypes.site,
                values: [],
                dataMethod: getSiteNamesWithPagination,
                searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
            }]);
        })();
    }, [])

    const showAddEditModal = async (presetInputId: string) => {
        setPresetInputId(presetInputId);
        setShowModal(!showModal);
    }

    const getCustomModifier = (): CustomRowModifierWithRange | undefined => {
        if (currentItemDetails.pSysnameType === PRESET_SYSNAME_TYPE.EL_LOCKS_ENTRY) {
            /**
                EL locks entry profile have default inputs that can't be modified so 
                we need to block delete and edit on the UI.
                Handle down, Key Cylinder, Bolt In, Bolt Out, and Trigger
             */
            return { fieldName: "inputNo", excludedValues: ["1", "2", "5", "6", "7"] };
        }

        return undefined;
    }

    const handleSave = (formFields, createNew) => {
        savePreset({ ...formFields, profileId: itemId }, createNew)
            .then(success => {
                toast.success(translate.byKey(TranslationKeys.presetSaved));
                onSave(createNew);
                dataRefresh((query?: ListQuery) => getPresetInputsByProfileId(itemId, translate, query));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) })
            });
        setPresetInputId('');
    }

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    return <TableContextProvider
        dataMethod={(listParameters?: ListQuery) => getPresetInputsByProfileId(itemId, translate, listParameters)}
        manualDataRefresh={manualDataRefresh}
        tableDescriptor={presetInputColumnDescriptor}
        searchDescriptor={presetInputSearchDescriptor}
        useTable
        usePagination
        useSearch
        useSort
        useFilter
        useLegend
        legendItems={getDeviceSettingsTableLegendItems(translate)}
        deleteMethod={onDelete}
        editMethod={showAddEditModal}
        maxPageSize={DEF_PG_SIZE_SUB}
        lookup={lookup}
        customDeleteWithRange={getCustomModifier()}
        customEditWithRange={getCustomModifier()} >
        <TableTab
            title={title}
            selectRows={false}
            customReportDescription={`inputs for preset "${currentItemDetails?.name}" on site "${currentItemDetails?.site}"`}
            cyName="presetInputs">
            <button
                type="button"
                className="flex px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                onClick={() => showAddEditModal('')}>
                {translate.byKey(TranslationKeys.add)}
            </button>
            <PresetSettingModal
                isOpen={showModal}
                onClose={() => showAddEditModal('')}
                title={presetInputId ? translate.byKey(TranslationKeys.editInputPreset) : translate.byKey(TranslationKeys.newInputPreset)}
                presetSettingId={presetInputId}
                systemProfileId={profileId}
                presetTemplateType={SelectType.presetInputTemplate}
                onSave={handleSave} />
            {children}
        </TableTab>
    </TableContextProvider>
};

export default PresetInputs;
